import { useMemo } from "react";
import { Flex, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { formatINR } from "@/shared/utils/format.utils";

type InvestmentRow = {
  id: string;
  amcName: string | null;
  schemeName: string;
  goalName: string | null;
  goalAmount: number | null;
  folioNumber: string | null;
  invested: number | null;
  currentValue: number | null;
  purchasedNav: number | null;
  currentNav: number | null;
};

type Props = {
  orders: any[]; // lumpsums from getById (aliased as `orders`)
  sxps: any[];
};

export default function InvestmentsTab({ orders, sxps }: Props) {
  const theme = useMantineTheme();

  const rows: InvestmentRow[] = useMemo(() => {
    const lumpsumRows: InvestmentRow[] = orders.map((l) => ({
      id: `lumpsum:${l.id}`,
      amcName: l.masterScheme?.scheme_amc_name ?? null,
      schemeName: l.masterScheme?.name ?? l.scheme ?? "—",
      goalName: l.goal?.goalName ?? null,
      goalAmount: l.goal?.goalAmount ?? null,
      folioNumber: l.folio ?? null,
      invested: l.amount ?? null,
      currentValue: null,
      purchasedNav: l.pruchase_time_nav ?? null,
      currentNav: l.masterScheme?.current_day_nav ?? null,
    }));

    const sxpRows: InvestmentRow[] = sxps.map((s) => {
      const installmentCount = (s.orders ?? []).length;
      const perInstallment = s.amount ?? null;
      const invested =
        perInstallment != null && installmentCount > 0 ? perInstallment * installmentCount : null;
      return {
        id: `sxp:${s.id}`,
        amcName: s.masterScheme?.scheme_amc_name ?? null,
        schemeName: s.masterScheme?.name ?? s.srcScheme ?? "—",
        goalName: s.goal?.goalName ?? null,
        goalAmount: s.goal?.goalAmount ?? null,
        folioNumber: null,
        invested,
        currentValue: null,
        purchasedNav: null,
        currentNav: s.masterScheme?.current_day_nav ?? null,
      };
    });

    return [...lumpsumRows, ...sxpRows];
  }, [orders, sxps]);

  const dash = <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;

  const columns: MRT_ColumnDef<InvestmentRow>[] = useMemo(
    () => [
      {
        accessorKey: "amcName",
        header: "AMC Name",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 220,
        Cell: ({ cell }) => cell.getValue<string>() || dash,
      },
      {
        accessorKey: "goalName",
        header: "Goal Name",
        size: 180,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "goalAmount",
        header: "Goal Amount",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "folioNumber",
        header: "Folio Number",
        size: 150,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "invested",
        header: "Invested",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "currentValue",
        header: "Current Value",
        size: 140,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: () => dash,
      },
      {
        accessorKey: "purchasedNav",
        header: "Purchased NAV",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v.toFixed(4);
        },
      },
      {
        accessorKey: "currentNav",
        header: "Current NAV",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v == null ? dash : v.toFixed(4);
        },
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All investments
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          density: "xs",
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
